/**
 * AppStateContext — global workspace selection.
 *
 * Hierarchy: Team → Workspace (Live/Test) → Folder → Template
 *
 * - Exactly two workspaces exist per team (Live, Test), seeded by the API.
 * - Selection persists to localStorage under docm:currentWorkspaceId.
 * - Loaded only while someone is signed in (keyed on the AuthContext user), cleared on sign-out.
 *   Public pages make no workspace request at all, and every way of signing in (password, MFA,
 *   SSO, accepting an invite) loads workspaces the same way, with no page having to ask.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { workspacesApiService } from '../services/workspacesApiService'
import { useAuth } from './AuthContext'
import { setActiveWorkspaceId } from '../services/BaseApiService'
import type { Workspace } from '../types'

interface AppStateValue {
  workspaces: Workspace[]
  currentWorkspace: Workspace | null
  setCurrentWorkspace: (workspace: Workspace) => void

  loading: boolean
  reload: () => Promise<void>
}

const AppStateContext = createContext<AppStateValue | null>(null)

const LS_WORKSPACE = 'docm:currentWorkspaceId'
/** Written by the removed program picker; cleared once so it doesn't linger forever. */
const LS_LEGACY_PROGRAM = 'docm:currentProgramId'

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null)
  const [loading, setLoading] = useState(true)
  const { user, loading: authLoading } = useAuth()
  // Auditors belong to no team, so they have no workspaces; nor does an account that left its last team. The team is
  // part of the key, so joining or creating one loads its workspaces.
  const sessionKey = user && !user.isAuditor && user.teamId ? `${user.userId}:${user.teamId}` : null

  const load = useCallback(async () => {
    setLoading(true)
    try {
      localStorage.removeItem(LS_LEGACY_PROGRAM)

      const workspacesResult = await workspacesApiService.ensure()
      setWorkspaces(workspacesResult)

      // Restore or default workspace selection
      const storedWorkspaceId = localStorage.getItem(LS_WORKSPACE)
      const workspace =
        workspacesResult.find(w => w.workspaceId === storedWorkspaceId) ??
        workspacesResult.find(w => !w.isTest) ??  // prefer Live
        workspacesResult[0] ?? null

      if (!workspace) return

      applyWorkspace(workspace)
    } catch {
      // API unavailable — stay empty
    } finally {
      setLoading(false)
    }
  }, [])

  // Sets the X-Workspace-Id header synchronously, before setCurrentWorkspaceState schedules a
  // re-render. This must NOT be deferred into a useEffect: the outlet remount it triggers (see
  // AppLayout's outletKey) mounts the new page and fires its fetch effect in the same commit,
  // and child effects run before parent effects -- a provider-level effect updating the header
  // would still be stale when that fetch goes out, scoping it to the workspace just left.
  function applyWorkspace(workspace: Workspace) {
    setActiveWorkspaceId(workspace.workspaceId)
    setCurrentWorkspaceState(workspace)
    localStorage.setItem(LS_WORKSPACE, workspace.workspaceId)
  }

  useEffect(() => {
    if (authLoading) return
    if (sessionKey) { load(); return }
    // Signed out (or an auditor): drop the previous session's workspaces and stop scoping requests.
    setActiveWorkspaceId(null)
    setWorkspaces([])
    setCurrentWorkspaceState(null)
    setLoading(false)
  }, [authLoading, sessionKey, load])

  return (
    <AppStateContext.Provider value={{
      workspaces,
      currentWorkspace,
      setCurrentWorkspace: applyWorkspace,
      loading,
      reload: load,
    }}>
      {children}
    </AppStateContext.Provider>
  )
}

export function useAppState(): AppStateValue {
  const ctx = useContext(AppStateContext)
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider')
  return ctx
}

/**
 * Reflects the Test/Live workspace onto <html data-theme> so the whole UI (including modals/portals) retints via CSS
 * custom properties. Called by the signed-in shell (AppLayout) only: public pages (sign-in, sign-up, marketing) stay
 * teal even when a session that last used Test is still live. Purely cosmetic -- the X-Workspace-Id header is synced
 * synchronously in applyWorkspace(), not here (see its comment).
 */
export function useWorkspaceTheme(): void {
  const ctx = useContext(AppStateContext)
  const test = !!ctx?.currentWorkspace?.isTest
  useEffect(() => {
    const el = document.documentElement
    const meta = document.querySelector('meta[name="theme-color"]')
    if (test) el.setAttribute('data-theme', 'test')
    else el.removeAttribute('data-theme')
    meta?.setAttribute('content', test ? '#C2410C' : '#0F9D92')
    return () => {
      el.removeAttribute('data-theme')
      meta?.setAttribute('content', '#0F9D92')
    }
  }, [test])
}

/**
 * True when the Live workspace is selected. Live is read-only: it shows only each template's
 * promoted version, and every change is made in Test and promoted. False outside a provider
 * (and before workspaces load), so pages rendered standalone in tests behave as Test.
 */
export function useIsLiveWorkspace(): boolean {
  const ctx = useContext(AppStateContext)
  return !!ctx?.currentWorkspace && !ctx.currentWorkspace.isTest
}
